import { SiteNav, SiteFooter } from '@/components/ui/SiteNav'
import { FileText } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/Button'

export function MethodologyPage() {
  return (
    <div className="min-h-screen bg-paper">
      <SiteNav />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-16 space-y-16">

        {/* Header */}
        <div className="space-y-4">
          <p className="text-sm font-medium text-echo-indigo uppercase tracking-wide">How it works</p>
          <h1 className="text-4xl font-medium text-deep-ink leading-tight" style={{ letterSpacing: '-0.02em' }}>
            Five questions sceptical buyers ask about AI accuracy — answered
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed">
            This page is a transparency document. Every question below is one we hear from buyers evaluating whether to trust a twin-panel result. The answers describe the actual mechanism — not a marketing claim.
          </p>
        </div>

        {/* Q1 */}
        <section className="space-y-4">
          <p className="font-semibold text-deep-ink text-lg">
            How do you measure accuracy?
          </p>
          <p className="text-slate-600 leading-relaxed">
            Before any twin participates in a bulk query, a subset of each respondent's answers is withheld from training — the holdout set. The twin is built on the remaining answers, then asked to predict the held-out answers it has never seen. Correct predictions divided by total held-out questions yields the raw accuracy score for that twin.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Accuracy is reported using the Wilson score interval at 95% confidence (z = 1.96). This interval is conservative for small holdout sets — it shrinks toward 0.5 when the total number of held-out questions is low. A twin requires at least five held-out questions to receive a <code className="text-sm bg-slate-100 px-1 rounded">validated</code> status; fewer than five yields <code className="text-sm bg-slate-100 px-1 rounded">low_confidence</code>.
          </p>
          <p className="text-slate-600 leading-relaxed">
            The holdout split is deterministic: seeded on <code className="text-sm bg-slate-100 px-1 rounded">(twin_id + upload_id)</code>. Identical inputs always produce identical splits. You can inspect the holdout configuration in your project's methodology settings.
          </p>
          <p className="text-sm text-slate-400 italic border-t border-slate-100 pt-3 mt-4">
            These are research signals to inform your review process. Echosome is decision support,
            not a compliance approval. Final review stays with your legal and compliance teams.
          </p>
        </section>

        {/* Q2 */}
        <section className="space-y-4">
          <p className="font-semibold text-deep-ink text-lg">
            What data does the twin use?
          </p>
          <p className="text-slate-600 leading-relaxed">
            Each twin is built exclusively from that respondent's own prior answers in the uploaded CSV. No demographic inferences are made from name, email, or other identifying fields. No external data sources — panels, third-party profiles, or public datasets — are used to augment a twin's knowledge.
          </p>
          <p className="text-slate-600 leading-relaxed">
            This constraint is intentional: the twin's predictive ability is bounded by what the respondent actually told you. If a respondent answered 12 questions and you ask the twin a 13th, the twin reasons from those 12 answers only. This keeps every prediction grounded in first-party evidence and makes the source of any prediction fully auditable.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Source quotes in twin responses carry a <code className="text-sm bg-slate-100 px-1 rounded">source_text_is_untrusted: true</code> marker — they are evidence, not verified statements. Treat them as reasoning prompts, not as direct quotations.
          </p>
        </section>

        {/* Q3 */}
        <section className="space-y-4">
          <p className="font-semibold text-deep-ink text-lg">
            What happens when the model is uncertain?
          </p>
          <p className="text-slate-600 leading-relaxed">
            When a twin's confidence falls below the configured floor — by default, a Wilson lower bound below 0.40 — the prediction is abstained rather than forced. Abstained predictions are excluded from the accuracy denominator entirely: the denominator is total scored predictions, not total predictions attempted.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Every accuracy report shows the abstention count explicitly. You will see: total twins, scored twins, abstained twins, and the accuracy figure computed over scored twins only. The abstention count is not a failure metric — it is a correctness feature that prevents overconfident outputs from degrading aggregate accuracy.
          </p>
          <p className="text-slate-600 leading-relaxed">
            In bulk query results, abstained predictions are labeled in the per-twin breakdown. If a large fraction of twins abstained on a specific question, that is a signal the question may be outside the scope of the respondents' prior answers — worth flagging before citing the result externally.
          </p>
        </section>

        {/* Q4 */}
        <section className="space-y-4">
          <p className="font-semibold text-deep-ink text-lg">
            Can I see the misses?
          </p>
          <p className="text-slate-600 leading-relaxed">
            Yes. Every accuracy report includes the full miss list — the held-out questions where the twin's prediction did not match the respondent's real answer. The miss list is accessible at <code className="text-sm bg-slate-100 px-1 rounded">/app/projects/:id/twin-panel/accuracy/misses</code> for any project with a completed backtest.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Misses are not hidden or averaged away. They are used to compute the holdout quality score for each twin and to inform which question types are less reliably predicted for a given respondent profile. If you see a pattern in the misses — for example, all misses clustered on a specific question topic — that tells you something about where the twin's knowledge is thin.
          </p>
          <p className="text-slate-600 leading-relaxed">
            When presenting results to clients, showing the miss list alongside the accuracy figure is the responsible approach. A 75% accuracy claim with a visible miss list is more credible than a 75% claim with no supporting detail.
          </p>
        </section>

        {/* Q5 */}
        <section className="space-y-4">
          <p className="font-semibold text-deep-ink text-lg">
            How do I audit this for my clients?
          </p>
          <p className="text-slate-600 leading-relaxed">
            The governance template below is a process document for recording validation cycles. It covers: holdout configuration, the validation cycle log (what to record per run), how to interpret Wilson CIs for reviewers unfamiliar with the statistic, and an audit checklist a reviewer can sign off.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Per-twin accuracy is inspectable at the project level: every twin has a status (<code className="text-sm bg-slate-100 px-1 rounded">validated</code>, <code className="text-sm bg-slate-100 px-1 rounded">low_confidence</code>, or <code className="text-sm bg-slate-100 px-1 rounded">excluded</code>), a Wilson CI, and a holdout question count. These figures come from your data, not from cross-project benchmarks.
          </p>
          <p className="text-slate-600 leading-relaxed">
            Every Echosome deliverable that cites accuracy figures should include the regulated-industry disclaimer below. The governance template's audit checklist confirms this before you share a report externally.
          </p>
          <p className="text-sm text-slate-400 italic border-t border-slate-100 pt-3 mt-4">
            These are research signals to inform your review process. Echosome is decision support,
            not a compliance approval. Final review stays with your legal and compliance teams.
          </p>

          {/* Governance download */}
          <div className="pt-2">
            <a
              href="/governance-template.md"
              download="synthetic-research-governance-template.md"
              className="inline-flex items-center gap-2 text-sm text-echo-indigo underline hover:text-echo-indigo/80"
            >
              <FileText size={14} />
              Download governance template (.md)
            </a>
          </div>
        </section>

        {/* Footer CTA */}
        <div className="border border-border-gray rounded-xl p-5 space-y-3">
          <p className="text-sm text-slate-600">
            Questions about the twin-panel methodology, holdout configuration, or regulated-industry requirements?
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/security"><Button variant="outline" size="sm">Security overview</Button></Link>
            <Link to="/app/demo"><Button variant="primary" size="sm">Try a live demo</Button></Link>
          </div>
        </div>

      </main>
      <SiteFooter />
    </div>
  )
}
